import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFireFlameCurved, 
  faWifi, 
  faBuilding,
  faCheck,
  faPhone,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import { offerte, serviziBusiness, contattiAzienda, OffertaBase } from '../utils/data';

const Offerte: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'luce' | 'gas' | 'internet' | 'business'>('luce');

  const handleTabChange = (tab: 'luce' | 'gas' | 'internet' | 'business') => {
    setActiveTab(tab);
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'faLightbulb': return faLightbulb;
      case 'faFireFlameCurved': return faFireFlameCurved;
      case 'faWifi': return faWifi;
      default: return faLightbulb;
    }
  };

  const tabs = [
    { key: 'luce' as const, label: 'Offerte Luce', icon: faLightbulb },
    { key: 'gas' as const, label: 'Offerte Gas', icon: faFireFlameCurved },
    { key: 'internet' as const, label: 'Offerte Internet', icon: faWifi },
    { key: 'business' as const, label: 'Soluzioni Business', icon: faBuilding },
  ];

  const renderOfferteGrid = (offerteList: OffertaBase[], title: string, subtitle: string) => (
    <motion.div 
      key={title}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-12">
        <h2 className="text-3xl font-extrabold mb-4 text-light-text font-display">{title}</h2>
        <p className="text-lg text-light-muted max-w-2xl mx-auto">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {offerteList.map((offerta, index) => (
          <motion.div 
            key={offerta.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
            className={`rounded-2xl overflow-hidden ${offerta.popolare ? 'border-2 border-primary shadow-card-hover relative' : 'border border-light-border/50 shadow-card hover:shadow-card-hover transition-all duration-300'}`}
          >
            {offerta.popolare && (
              <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-2 px-4 text-sm font-semibold text-center">
                Più Richiesta
              </div>
            )}
            <div className="bg-white p-8 text-center">
              <h3 className="text-2xl font-bold mb-2 text-light-text font-display">{offerta.titolo}</h3>
              <p className="text-light-muted text-sm mb-6">{offerta.descrizione}</p>
              <div className="mb-2">
                <span className="text-5xl font-extrabold text-primary font-display">
                  {offerta.prezzo.toFixed(2).replace('.', ',')}€
                </span>
                <span className="text-light-muted text-sm ml-1">{offerta.unitaMisura}</span>
              </div>
              <p className="text-xs text-light-muted mb-6">Prezzo indicativo soggetto a variazioni</p>

              <ul className="space-y-3 text-left mb-8">
                {offerta.caratteristiche.map((caratteristica, i) => (
                  <li key={i} className="flex items-center">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                      <FontAwesomeIcon icon={faCheck} className="text-primary text-xs" />
                    </div>
                    <span className="text-light-text text-sm">{caratteristica}</span>
                  </li>
                ))}
              </ul>

              <motion.a 
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href={`tel:${contattiAzienda.telefonoPrincipale}`}
                className={`btn w-full ${offerta.popolare ? 'btn-primary' : 'btn-outline'}`}
              >
                <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Informazioni
              </motion.a>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-10 left-20 w-[400px] h-[400px] bg-secondary rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-20 w-[500px] h-[500px] bg-accent rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Confronta e scegli</span>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 font-display">Offerte a Confronto</h1>
            <p className="text-xl text-white/75 mb-4">
              Confronta le offerte disponibili sul mercato per luce, gas e internet.
              Trova la soluzione più adatta alle tue esigenze.
            </p>
            <p className="text-sm text-white/50">
              Le tariffe mostrate hanno carattere indicativo. La sottoscrizione è sempre facoltativa e a discrezione del cliente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-24">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center mb-16 gap-2"
          >
            {tabs.map((tab) => (
              <motion.button 
                key={tab.key}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
                className={`px-6 py-3 text-sm font-semibold transition-all duration-300 rounded-xl ${
                  activeTab === tab.key 
                    ? 'bg-primary text-white shadow-btn' 
                    : 'bg-white text-light-muted hover:text-primary border border-light-border/50 shadow-sm'
                }`}
                onClick={() => handleTabChange(tab.key)}
              >
                <FontAwesomeIcon icon={tab.icon} className="mr-2" /> {tab.label}
              </motion.button>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            {activeTab === 'luce' && renderOfferteGrid(
              offerte.luce, 'Offerte Luce',
              'Confronta le tariffe luce disponibili. I prezzi sono indicativi e la scelta è sempre a tua discrezione.'
            )}
            {activeTab === 'gas' && renderOfferteGrid(
              offerte.gas, 'Offerte Gas',
              'Confronta le offerte gas sul mercato. Nessun obbligo di sottoscrizione.'
            )}
            {activeTab === 'internet' && renderOfferteGrid(
              offerte.internet, 'Offerte Internet',
              'Scopri le soluzioni internet disponibili nella tua zona. Consulenza gratuita e senza vincoli.'
            )}

            {activeTab === 'business' && (
              <motion.div 
                key="business"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-extrabold mb-4 text-light-text font-display">Soluzioni Business</h2>
                  <p className="text-lg text-light-muted max-w-2xl mx-auto">
                    Consulenza dedicata per la tua attività, con analisi personalizzata delle esigenze e confronto offerte.
                  </p>
                </div>

                <div className="bg-white p-10 rounded-2xl shadow-card border border-light-border/50 mb-12">
                  <h3 className="text-2xl font-bold mb-4 text-light-text font-display">Consulenza Personalizzata per Aziende</h3>
                  <p className="mb-4 text-light-muted leading-relaxed">
                    Per le aziende offriamo un servizio di consulenza dedicato che analizza i consumi specifici 
                    della tua attività e confronta le offerte dei principali fornitori sul mercato.
                  </p>
                  <p className="mb-6 text-light-muted leading-relaxed">
                    I nostri consulenti business sono a disposizione per un'analisi gratuita e senza impegno 
                    dei tuoi consumi. La decisione di aderire a un'offerta rimane sempre a tua discrezione.
                  </p>
                  <div className="flex justify-center mt-8">
                    <motion.a 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href={`tel:${contattiAzienda.telefonoPrincipale}`}
                      className="btn btn-primary"
                    >
                      <FontAwesomeIcon icon={faPhone} className="mr-2" /> Richiedi Consulenza Gratuita
                    </motion.a>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                  {serviziBusiness.map((servizio, index) => (
                    <motion.div 
                      key={servizio.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -8, transition: { duration: 0.2 } }}
                      className="card text-center"
                    >
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-6">
                        <FontAwesomeIcon icon={getIconComponent(servizio.icona)} className="text-2xl" />
                      </div>
                      <h3 className="text-lg font-bold mb-3 text-light-text font-display">{servizio.titolo}</h3>
                      <p className="text-light-muted text-sm leading-relaxed">{servizio.descrizione}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Disclaimer Offerte */}
          <div className="mt-16 p-6 rounded-2xl bg-light-background border border-light-border/50 flex items-start gap-4">
            <FontAwesomeIcon icon={faInfoCircle} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-light-muted text-xs leading-relaxed">
              <strong className="text-light-text">Nota informativa:</strong> I prezzi e le condizioni riportati sono indicativi 
              e soggetti a variazioni da parte dei fornitori. Le offerte presentate hanno scopo puramente informativo e comparativo. 
              Rinova Srl non è un fornitore di energia, gas o servizi internet, ma un intermediario che facilita il confronto 
              tra le offerte disponibili. La sottoscrizione di qualsiasi contratto è facoltativa e a totale discrezione del cliente. 
              Per i dettagli contrattuali completi si rimanda alla documentazione ufficiale dei singoli fornitori.
            </p>
          </div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-24"
          >
            <CallToAction 
              title="Hai bisogno di aiuto per confrontare?" 
              subtitle="I nostri consulenti sono a tua disposizione per una consulenza gratuita e senza obbligo di sottoscrizione"
              variant="secondary"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Offerte;
